import { RoutePlannerStore } from "../../state/route-planner-store";
import { RegionManagementStore } from "../../state/region-management-store";
import { ReactNode } from "react";
import {
  AdminAllLocationsCustomerResDto,
  AdminGetRoutesRegionResDto,
  AdminGetRoutesLocationsResDto,
  RouteStartingPointDto,
  AdminGetRouteTravelTimeResDto,
  MeasurementResDto,
} from "service/api/estabrookSchemas";

export enum LassoMode {
  REPLACE = "replace",
  ADD = "add",
  REMOVE = "remove",
}

export type RegionManagementMapTypes = Pick<
  RegionManagementStore,
  | "addresses"
  | "selectedAddresses"
  | "mapZoom"
  | "drawing"
  | "query"
  | "mapCentering"
  | "setSidebar"
  | "setMapZoom"
  | "setQuery"
  | "setSelectedAddress"
  | "toggleDrawing"
  | "lastSequence"
  | "setAddressSequence"
>;

export type RouteManagementMapTypes = Pick<
  RoutePlannerStore,
  // | "addresses"
  // | "selectedAddresses"
  // | "setSelectedAddress"
  | "mapZoom"
  | "sidebar"
  | "query"
  | "mapCentering"
  | "setSidebar"
  | "setMapZoom"
  | "setQuery"
  | "toggleDrawing"
>;

export type CombinedMapProps = RegionManagementMapTypes &
  RouteManagementMapTypes;

export enum OrderStatuses {
  Regular = "regular",
  Skipped = "skipped",
  Delayed = "delayed",
  Urgent = "urgent",
}
export interface OrderStatus {
  status: OrderStatuses;
}

export interface MapMarker {
  id: string;
  latitude?: number;
  longitude?: number;
  address?: string;
  color?: string;
  selected?: boolean;
  duration?: string | number;
  sequence?: number;
  locationSequence?: number | null;
  status?: string;
  customerType?: "onDemand" | "annual";
  customer?: AdminAllLocationsCustomerResDto;
  region?: AdminGetRoutesRegionResDto;
  routeId?: string;
  routeName?: string;
  driverName?: string;
  order?: OrderStatus;
  driverStatus?: AdminGetRoutesLocationsResDto["driverStatus"];
  driverStatusLastUpdatedAt?: string | null;
  driverStatusChangeReason?: AdminGetRoutesLocationsResDto["driverStatusChangeReason"];
  driverStatusChangeReasonComment?: string;
  locationNoteCount?: number;
  serviceAccess?: boolean; // Address-level service access
  // Customer type flags for notification recipients
  isFlatRate?: boolean;
  isOnDemand?: boolean;
  isSnowReporter?: boolean;
  // Travel details for estimated arrival calculation
  travelDetails?: AdminGetRouteTravelTimeResDto;
  routeStartTime?: string;
  // Current accumulated snow amount at the location (EST Snow)
  snowAmount?: MeasurementResDto;
  // Last time this location was plowed (completed or partially completed)
  lastPlowedAt?: string | null;
}

export type DriverLiveLocation = {
  longitude: number;
  latitude: number;
};

export interface DriverMarker {
  driverId?: string | null;
  color?: string;
  startingPoint?: RouteStartingPointDto;
  // Earliest start time ("HH:mm") across the driver's routes
  startTime?: string;
  // ISO time the driver started their first stop of the day
  actualStartTime?: string;
  fullName?: string;
  estimatedTimeOfArrival?: number;
  disconnected?: boolean;
  liveLocation?: DriverLiveLocation;
}

export interface MapComponentProps {
  mapWidth: string;
  mapHeight: string;
  markers: MapMarker[];
  driverMarkers?: DriverMarker[];
  mapZoom: number;
  lastSequence?: number;
  mapCentering?: MapMarker[];
  drawing?: boolean;
  lassoMode?: LassoMode;
  sequencing?: boolean;
  selectedAddresses?: MapMarker[];
  sequencingUnassigned?: boolean;
  query?: string;
  mapFilters?: ReactNode;
  isLoading: boolean;
  isSearchLoading: boolean;
  searchedAddresses?: MapMarker[];
  showTopMapControl?: boolean;
  setQuery: (query: string) => void;
  setAddressSequence?: (
    id: string,
    sequence?: number,
    unassigned?: boolean,
  ) => void;
  setSelectedAddress?: (address: MapMarker[], multiSelect?: boolean) => void;
  onMarkerClick?: (
    marker: MapMarker,
    isShiftPressed: boolean,
    event?: google.maps.MapMouseEvent,
  ) => void;
  onSearchResultSelect?: (marker: MapMarker) => void;
  onMapZoomChange: (zoom: number) => void;
  onDrawingToggle: (isDrawing: boolean, mode?: LassoMode) => void;
  setMapCentering?: (id: string | undefined, addresses?: MapMarker[]) => void;
  setSidebar?: (sidebar: boolean) => void;
  onSelectedMarkersChange?: (markers: MapMarker[], event?: MouseEvent) => void;
  onResetSelection?: () => void;
  hasLiveStateChanges?: boolean;
  hasPlannedChanges?: boolean;
  existingSelection?: boolean;
  onAddCustomer?: (searchedAddress: string) => void;
}
